import { DestroyRef, type Signal, inject, linkedSignal } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  TranslateService,
  type InterpolatableTranslationObject,
  type TranslationObject,
} from '@ngx-translate/core';
import {
  resolveLocale,
  splitLocale,
  toSupportedLocale,
  type Language,
  type Locale,
} from '@shared/models/auth';
import { Storage } from '@tvd/utils/storage';
import type { Observable } from 'rxjs';
import { distinctUntilChanged, switchMap, tap } from 'rxjs';

/**
 * Configuration for {@link createLanguageSwitcher}. Everything the switcher
 * needs from the host that the host alone can supply — the seed (where the
 * active Language comes from), the acting user's id, and the user-PATCH call.
 * Persistence side-effects belong here, not in the presentational
 * `<tvd-language-picker>`, because the booking surfaces and the dashboard
 * nav-drawer drive different auth services.
 */
export interface LanguageSwitchConfig {
  /**
   * Seed for the `currentLang` linkedSignal — recomputed when its reactive
   * dependencies change (e.g. the logged-in Customer's locale resolving).
   * Defaults to the active translation language; only hosts that seed from
   * somewhere richer (e.g. the wizard's logged-in Customer locale) pass one.
   */
  seed?: () => Language;
  /** The acting user's id, or `undefined` for anonymous visitors. */
  userId: () => string | undefined;
  /** Persist the user's chosen locale. Caller wires its own auth service. */
  updateUser: (
    userId: string,
    patch: { locale: Locale },
  ) => Observable<unknown>;
}

export interface LanguageSwitch {
  /**
   * The active UI Language — a read-only view. Bound to
   * `<tvd-language-picker>`'s `[currentLang]` input; mutate only via
   * {@link LanguageSwitch.selectLang} so storage + the user PATCH stay in sync.
   */
  readonly currentLang: Signal<Language>;
  /**
   * Switch to `lang` and persist the user's locale (preserving the stored
   * culture when present), so untranslated keys fall back via ngx-translate.
   */
  selectLang(lang: Language): void;
}

/**
 * The single language-switch implementation shared by every booking-wizard
 * surface (the wizard shell + the booking summary). Must run inside an
 * injection context — it injects `TranslateService`, `Storage` and
 * `DestroyRef` itself. Parameterized by {@link LanguageSwitchConfig} so issue
 * 03 can reuse it from the nav-drawer with a different auth service.
 */
export function createLanguageSwitcher(
  config: LanguageSwitchConfig,
): LanguageSwitch {
  const translate = inject(TranslateService);
  const storage = inject(Storage);
  const destroyRef = inject(DestroyRef);

  const currentLang = linkedSignal<Language>(
    config.seed ??
      (() => splitLocale(translate.getCurrentLang() || 'de').language),
  );

  // Load + activate the bundle for the active Language, marking it as the
  // active translation (last arg `true`) so merges don't clobber it.
  toObservable(currentLang)
    .pipe(
      distinctUntilChanged(),
      switchMap((lang) =>
        translate.use(lang).pipe(
          tap((content: InterpolatableTranslationObject) =>
            // `use()` resolves an InterpolatableTranslationObject; setTranslation
            // re-narrows to TranslationObject (interpolation re-applied on merge).
            translate.setTranslation(lang, content as TranslationObject, true),
          ),
        ),
      ),
      takeUntilDestroyed(destroyRef),
    )
    .subscribe();

  function selectLang(lang: Language): void {
    currentLang.set(lang);

    // Preserve the culture the user already had stored; when storage is empty
    // (or carries a malformed `ru-` style locale with an empty region) fall
    // back to the Language's supported culture so we never persist a malformed
    // `ru-` — `ru` resolves to `ru-RU`, `en` to `en-GB`, `de` to `de-DE`.
    const stored = storage.get('USER_LOCALE');
    const storedCulture = stored ? splitLocale(stored).culture : '';
    const culture =
      storedCulture || splitLocale(toSupportedLocale(lang)).culture;
    const newLocale: Locale = resolveLocale(`${lang}-${culture}`);
    storage.set('USER_LOCALE', newLocale);

    const userId = config.userId();
    if (userId) {
      config.updateUser(userId, { locale: newLocale }).subscribe();
    }
  }

  return { currentLang, selectLang };
}
